import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormGroup,
  Badge,
} from "reactstrap";
import axios from "axios";
import { useHistory } from "react-router-dom";
import swal from "sweetalert";
import { API_TIMEOUT, API_DARAH_KELUAR, API_DONOR_DARAH } from "utils";
import { logApp } from "utils/func";

function DarahKeluar() {
  const [tabelData, setTabelData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [currentUser, setCurrentUser] = useState("");
  const history = useHistory();
  const [saveLoading, setSaveLoading] = useState(false);
  const [stokData, setStokData] = useState([]);

  // Form state
  const [id, setId] = useState("");
  const [tanggal, setTanggal] = useState("");
  const [jumlah_kantung, setJumlahKantung] = useState("");
  const [blood_type, setBloodType] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [oldJumlahKantung, setOldJumlahKantung] = useState(0);
  const [oldBloodType, setOldBloodType] = useState("");

  // Dropdown data
  const [listBloodTypes, setListBloodTypes] = useState([]);

  useEffect(() => {
    if (!currentUser) {
      let user = localStorage.getItem("user");
      if (user) {
        user = JSON.parse(user);
        setCurrentUser(user);
        logApp(user.uid, "Darah Keluar");
      } else {
        history.push("/login");
      }
    }
    getListBloodTypes();
    fetchStokData();
    fetchData();
  }, []);

  const fetchData = () => {
    axios({
      method: "get",
      url: API_DARAH_KELUAR,
      timeout: API_TIMEOUT,
    })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          // Normalisasi tanggal: konversi ISO + Z ke tanggal lokal (YYYY-MM-DD)
          const normalizedData = response.data.data.map((item) => {
            if (item.tanggal) {
              const str = String(item.tanggal);
              if (/^\d{4}-\d{2}-\d{2}T.*Z$/.test(str)) {
                item.tanggal = toLocalDateString(str);
              } else if (str.includes("T")) {
                item.tanggal = str.split("T")[0];
              } else if (str.includes(" ")) {
                item.tanggal = str.split(" ")[0];
              }
            }
            return item;
          });
          setTabelData(normalizedData);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        swal("Error", "Gagal mengambil data", "error");
      });
  };

  const fetchStokData = async () => {
    // Urutan prioritas:
    // 1) /stok-darah/all (per bloodTypeID termasuk rhesus)
    // 2) /stok-darah/detail
    // 3) /stok-darah (agregat dasar A/B/AB/O)
    const endpoints = [
      `${API_DONOR_DARAH}stok-darah/all`,
      `${API_DONOR_DARAH}stok-darah/detail`,
      `${API_DONOR_DARAH}stok-darah`,
    ];

    for (let i = 0; i < endpoints.length; i++) {
      try {
        const res = await axios.get(endpoints[i], { timeout: API_TIMEOUT });
        if (
          res.status === 200 &&
          res.data &&
          res.data.success &&
          res.data.data
        ) {
          setStokData(res.data.data);
          return;
        }
      } catch (err) {
        console.warn(`Fallback stok endpoint ke-${i + 1} gagal:`, err?.message);
        // lanjut ke endpoint berikutnya
      }
    }

    // Jika semua gagal
    console.error("Gagal mengambil data stok dari semua endpoint.");
  };

  const getListBloodTypes = async () => {
    try {
      const response = await axios.get(API_DONOR_DARAH + "bloodtypes");
      if (response.data.success && response.data.data) {
        setListBloodTypes(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching blood types:", error);
    }
  };

  const getStokByBloodType = (bloodTypeID) => {
    const id = parseInt(bloodTypeID);
    const stok = stokData.find((s) => {
      const stokId =
        s.bloodTypeID !== undefined ? parseInt(s.bloodTypeID) : undefined;
      const stokIdAlt =
        s.blood_type !== undefined ? parseInt(s.blood_type) : undefined;
      return stokId === id || stokIdAlt === id;
    });
    return stok ? stok.stok_kantung : 0;
  };

  // Helper: konversi ISO dengan Z ke YYYY-MM-DD lokal
  const toLocalDateString = (isoString) => {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format tanggal untuk input date (YYYY-MM-DD) tanpa bergeser timezone
  const formatDateInput = (value) => {
    if (!value) return "";
    const str = String(value).trim();

    // Jika sudah dalam format YYYY-MM-DD, langsung return
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return str;
    }

    // ISO dengan Z: gunakan local date (menghindari mundur 1 hari)
    if (/^\d{4}-\d{2}-\d{2}T.*Z$/.test(str)) {
      const local = toLocalDateString(str);
      if (local) return local;
    }

    // ISO tanpa Z: ambil bagian tanggal, fallback ke local date
    if (str.includes("T")) {
      const datePart = str.split("T")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
      const local = toLocalDateString(str);
      if (local) return local;
    }

    // Jika ada spasi (format datetime dengan spasi), ambil bagian tanggal
    if (str.includes(" ")) {
      const datePart = str.split(" ")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }

    // Format DD/MM/YYYY atau DD-MM-YYYY
    const datePattern1 = /^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/;
    const match1 = str.match(datePattern1);
    if (match1) {
      const [, day, month, year] = match1;
      return `${year}-${month}-${day}`;
    }

    // Format YYYY/MM/DD
    const datePattern2 = /^(\d{4})[\/](\d{2})[\/](\d{2})$/;
    const match2 = str.match(datePattern2);
    if (match2) {
      const [, year, month, day] = match2;
      return `${year}-${month}-${day}`;
    }

    // Fallback: ekstrak pattern YYYY-MM-DD
    const datePattern3 = /(\d{4}-\d{2}-\d{2})/;
    const match3 = str.match(datePattern3);
    if (match3) {
      return match3[1];
    }

    return str;
  };

  // Format tanggal untuk tampilan tabel tanpa bergeser timezone
  const formatDateDisplay = (value) => {
    if (!value) return "-";
    const str = String(value).trim();
    // Jika ISO dengan Z, gunakan tanggal lokal
    if (/^\d{4}-\d{2}-\d{2}T.*Z$/.test(str)) {
      const local = toLocalDateString(str);
      if (local) {
        return formatDateDisplay(local);
      }
    }
    // Ambil bagian tanggal saja
    const datePart = str.includes("T")
      ? str.split("T")[0]
      : str.includes(" ")
      ? str.split(" ")[0]
      : str;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (match) {
      const [, year, month, day] = match;
      const bulan = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "Mei",
        "Jun",
        "Jul",
        "Agu",
        "Sep",
        "Okt",
        "Nov",
        "Des",
      ];
      const monthName = bulan[parseInt(month, 10) - 1] || month;
      return `${day} ${monthName} ${year}`;
    }
    // Jika tidak sesuai pattern, kembalikan apa adanya
    return datePart;
  };

  const handleOpenModal = () => {
    resetForm();
    fetchStokData(); // Refresh stok sebelum buka modal
    // Tunggu sebentar untuk memastikan stok sudah ter-load
    setTimeout(() => {
      setModalOpen(true);
    }, 100);
  };

  const handleOpenEdit = (item) => {
    setId(item.id);
    // Format tanggal dengan benar, pastikan tidak ada timezone shift
    const formattedDate = formatDateInput(item.tanggal);
    console.log("Original tanggal:", item.tanggal, "Formatted:", formattedDate);
    setTanggal(formattedDate || "");
    setJumlahKantung(item.jumlah_kantung || "");
    setBloodType(item.blood_type || "");
    setKeterangan(item.keterangan || "");
    // Simpan nilai lama untuk perhitungan stok
    setOldJumlahKantung(item.jumlah_kantung || 0);
    setOldBloodType(item.blood_type || "");
    fetchStokData(); // Refresh stok sebelum buka modal
    setModalEdit(true);
  };

  // Filter golongan darah yang memiliki stok
  const availableBloodTypes = listBloodTypes.filter((bt) => {
    const stok = getStokByBloodType(bt.bloodTypeID);
    return stok > 0;
  });

  const resetForm = () => {
    setId("");
    setTanggal("");
    setJumlahKantung("");
    setBloodType("");
    setKeterangan("");
    setOldJumlahKantung(0);
    setOldBloodType("");
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalEdit(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tanggal || !jumlah_kantung || !blood_type) {
      swal(
        "Info",
        "Tanggal, jumlah kantung, dan golongan darah wajib diisi",
        "info"
      );
      return;
    }

    if (parseInt(jumlah_kantung) <= 0) {
      swal("Info", "Jumlah kantung harus lebih dari 0", "info");
      return;
    }

    // Cek stok tersedia
    const stokTersedia = getStokByBloodType(blood_type);
    if (stokTersedia <= 0) {
      swal(
        "Stok Tidak Tersedia",
        `Stok untuk golongan darah ini tidak tersedia (stok: ${stokTersedia} kantung)`,
        "error"
      );
      return;
    }
    if (stokTersedia < parseInt(jumlah_kantung)) {
      swal(
        "Stok Tidak Cukup",
        `Stok tersedia: ${stokTersedia} kantung, yang diminta: ${jumlah_kantung} kantung`,
        "error"
      );
      return;
    }

    setSaveLoading(true);

    const data = {
      tanggal: tanggal,
      jumlah_kantung: parseInt(jumlah_kantung),
      blood_type: parseInt(blood_type),
      id_petugas_input: currentUser.id || 1,
      keterangan: keterangan || null,
    };

    try {
      const response = await axios({
        method: "post",
        url: API_DARAH_KELUAR,
        data: data,
        timeout: API_TIMEOUT,
      });

      if (response.status === 201 && response.data.success) {
        swal(
          "Success",
          response.data.message || "Data berhasil ditambahkan",
          "success"
        );
        fetchData();
        fetchStokData(); // Refresh stok setelah keluar
        handleCloseModal();
      } else {
        swal(
          "Error",
          response.data.message || "Gagal menambahkan data",
          "error"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = error.response?.data?.message || "Terjadi kesalahan";
      swal("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!tanggal || !jumlah_kantung || !blood_type) {
      swal(
        "Info",
        "Tanggal, jumlah kantung, dan golongan darah wajib diisi",
        "info"
      );
      return;
    }

    if (parseInt(jumlah_kantung) <= 0) {
      swal("Info", "Jumlah kantung harus lebih dari 0", "info");
      return;
    }

    // Cek stok tersedia untuk update
    const stokTersedia = getStokByBloodType(blood_type);
    if (stokTersedia <= 0) {
      swal(
        "Stok Tidak Tersedia",
        `Stok untuk golongan darah ini tidak tersedia (stok: ${stokTersedia} kantung)`,
        "error"
      );
      return;
    }

    // Jika golongan darah berubah, cek stok untuk golongan darah baru
    if (oldBloodType && oldBloodType !== blood_type) {
      if (stokTersedia < parseInt(jumlah_kantung)) {
        swal(
          "Stok Tidak Cukup",
          `Stok tersedia: ${stokTersedia} kantung, yang diminta: ${jumlah_kantung} kantung`,
          "error"
        );
        return;
      }
    } else if (oldBloodType === blood_type) {
      // Jika golongan darah sama, hitung selisih jumlah
      const selisih = parseInt(jumlah_kantung) - parseInt(oldJumlahKantung);
      if (selisih > 0 && stokTersedia < selisih) {
        swal(
          "Stok Tidak Cukup",
          `Penambahan jumlah memerlukan ${selisih} kantung, tetapi stok tersedia hanya ${stokTersedia} kantung`,
          "error"
        );
        return;
      }
    }

    setSaveLoading(true);

    const data = {
      tanggal: tanggal,
      jumlah_kantung: parseInt(jumlah_kantung),
      blood_type: parseInt(blood_type),
      id_petugas_input: currentUser.id || 1,
      keterangan: keterangan || null,
    };

    try {
      const response = await axios({
        method: "put",
        url: `${API_DARAH_KELUAR}/${id}`,
        data: data,
        timeout: API_TIMEOUT,
      });

      if (response.status === 200 && response.data.success) {
        swal(
          "Success",
          response.data.message || "Data berhasil diupdate",
          "success"
        );
        fetchData();
        fetchStokData(); // Refresh stok setelah update
        handleCloseModal();
      } else {
        swal(
          "Error",
          response.data.message || "Gagal mengupdate data",
          "error"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      swal(
        "Error",
        error.response?.data?.message || "Terjadi kesalahan",
        "error"
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = (item) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data yang dihapus tidak dapat dikembalikan!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios({
            method: "delete",
            url: `${API_DARAH_KELUAR}/${item.id}`,
            timeout: API_TIMEOUT,
          });

          if (response.status === 200 && response.data.success) {
            swal(
              "Success",
              response.data.message || "Data berhasil dihapus",
              "success"
            );
            fetchData();
            fetchStokData(); // Refresh stok setelah hapus
          } else {
            swal(
              "Error",
              response.data.message || "Gagal menghapus data",
              "error"
            );
          }
        } catch (error) {
          console.error("Error:", error);
          swal(
            "Error",
            error.response?.data?.message || "Terjadi kesalahan",
            "error"
          );
        }
      }
    });
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <CardTitle tag="h4">
                  Data Darah Keluar <Badge color="danger">Bank Darah</Badge>
                </CardTitle>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Button
                    color="primary"
                    onClick={handleOpenModal}
                    disabled={availableBloodTypes.length === 0}
                  >
                    <i className="tim-icons icon-simple-add" /> Tambah Data
                  </Button>
                  {availableBloodTypes.length === 0 && (
                    <small className="text-danger">
                      Tidak ada stok tersedia untuk dikeluarkan
                    </small>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                <Table className="tablesorter" responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>No</th>
                      <th>Tanggal</th>
                      <th>Golongan Darah</th>
                      <th>Jumlah Kantung</th>
                      <th>Keterangan</th>
                      <th>Petugas</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabelData && tabelData.length > 0 ? (
                      tabelData.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{formatDateDisplay(item.tanggal)}</td>
                          <td>
                            <Badge color="info">
                              {item.golonganDarah || "-"}
                            </Badge>
                          </td>
                          <td>{item.jumlah_kantung}</td>
                          <td>{item.keterangan || "-"}</td>
                          <td>{item.nama_petugas || "-"}</td>
                          <td>
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => handleOpenEdit(item)}
                              style={{ marginRight: 5 }}
                            >
                              <i className="tim-icons icon-pencil" />
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(item)}
                            >
                              <i className="tim-icons icon-simple-remove" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          Tidak ada data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Tambah */}
        <Modal isOpen={modalOpen} toggle={handleCloseModal} size="lg">
          <ModalHeader toggle={handleCloseModal}>
            Tambah Data Darah Keluar
          </ModalHeader>
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormGroup>
                <Label>Tanggal *</Label>
                <Input
                  type="date"
                  value={tanggal}
                  onChange={(e) => setTanggal(e.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Golongan Darah *</Label>
                <Input
                  type="select"
                  value={blood_type}
                  onChange={(e) => {
                    setBloodType(e.target.value);
                  }}
                  required
                >
                  <option value="">Pilih Golongan Darah</option>
                  {availableBloodTypes.length > 0 ? (
                    availableBloodTypes.map((bt) => {
                      const stok = getStokByBloodType(bt.bloodTypeID);
                      return (
                        <option key={bt.bloodTypeID} value={bt.bloodTypeID}>
                          {bt.golonganDarah} (Stok: {stok} kantung)
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>
                      Tidak ada stok tersedia
                    </option>
                  )}
                </Input>
                {blood_type ? (
                  getStokByBloodType(blood_type) > 0 ? (
                    <small className="text-muted">
                      Stok tersedia: {getStokByBloodType(blood_type)} kantung
                    </small>
                  ) : (
                    <small className="text-danger">
                      Stok tidak tersedia untuk golongan darah ini
                    </small>
                  )
                ) : (
                  <small className="text-muted">
                    Pilih golongan darah yang memiliki stok tersedia
                  </small>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Jumlah Kantung *</Label>
                <Input
                  type="number"
                  min="1"
                  max={blood_type ? getStokByBloodType(blood_type) : undefined}
                  value={jumlah_kantung}
                  onChange={(e) => setJumlahKantung(e.target.value)}
                  required
                />
                {blood_type && (
                  <small className="text-muted">
                    Maksimal: {getStokByBloodType(blood_type)} kantung
                  </small>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Keterangan</Label>
                <Input
                  type="text"
                  value={keterangan}
                  onChange={(e) => setKeterangan(e.target.value)}
                  placeholder="Contoh: Keluar untuk pasien Yanto (ReqID: 2)"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={handleCloseModal}>
                Batal
              </Button>
              <Button color="primary" type="submit" disabled={saveLoading}>
                {saveLoading ? "Menyimpan..." : "Simpan"}
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Modal Edit */}
        <Modal isOpen={modalEdit} toggle={handleCloseModal} size="lg">
          <ModalHeader toggle={handleCloseModal}>
            Edit Data Darah Keluar
          </ModalHeader>
          <form onSubmit={handleUpdate}>
            <ModalBody>
              <FormGroup>
                <Label>Tanggal *</Label>
                <Input
                  type="date"
                  value={tanggal}
                  onChange={(e) => setTanggal(e.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Golongan Darah *</Label>
                <Input
                  type="select"
                  value={blood_type}
                  onChange={(e) => setBloodType(e.target.value)}
                  required
                >
                  <option value="">Pilih Golongan Darah</option>
                  {availableBloodTypes.length > 0 ? (
                    availableBloodTypes.map((bt) => {
                      const stok = getStokByBloodType(bt.bloodTypeID);
                      return (
                        <option key={bt.bloodTypeID} value={bt.bloodTypeID}>
                          {bt.golonganDarah} (Stok: {stok} kantung)
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>
                      Tidak ada stok tersedia
                    </option>
                  )}
                </Input>
                {blood_type ? (
                  getStokByBloodType(blood_type) > 0 ? (
                    <small className="text-muted">
                      Stok tersedia: {getStokByBloodType(blood_type)} kantung
                    </small>
                  ) : (
                    <small className="text-danger">
                      Stok tidak tersedia untuk golongan darah ini
                    </small>
                  )
                ) : (
                  <small className="text-muted">
                    Pilih golongan darah yang memiliki stok tersedia
                  </small>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Jumlah Kantung *</Label>
                <Input
                  type="number"
                  min="1"
                  value={jumlah_kantung}
                  onChange={(e) => setJumlahKantung(e.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Keterangan</Label>
                <Input
                  type="text"
                  value={keterangan}
                  onChange={(e) => setKeterangan(e.target.value)}
                  placeholder="Contoh: Keluar untuk pasien Yanto (ReqID: 2)"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={handleCloseModal}>
                Batal
              </Button>
              <Button color="primary" type="submit" disabled={saveLoading}>
                {saveLoading ? "Mengupdate..." : "Update"}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </div>
    </>
  );
}

export default DarahKeluar;
